// src/pages/AmazonSettingsPage.tsx
//
// Amazon Seller Central 連携設定ページ。
// Seller ID を入力・保存し、MallContext の shops を更新する。

import React, { useState, useEffect } from "react";
import { useMallContext } from "../contexts/MallContext";
import { shopRefFromSellerId } from "../types/mall";
import { apiDeleteJson, apiPostJson } from "../utils/apiClient";

function getAmazonStorageScope(): { tenantId: string; companyId: string } {
    if (typeof window === "undefined") {
        return { tenantId: "ecvision", companyId: "default" };
    }
    const tenantId = String(localStorage.getItem("selectedTenant") || "ecvision").trim() || "ecvision";
    const companyId = String(
        localStorage.getItem("selectedCompanyId") ||
        localStorage.getItem("companyId") ||
        localStorage.getItem("accountId") ||
        "default"
    ).trim() || "default";
    return { tenantId, companyId };
}

function getAmazonStorageKey(baseKey: "amazonSellerId" | "amazonSellerName"): string {
    const scope = getAmazonStorageScope();
    return `${baseKey}:${scope.tenantId}:${scope.companyId}`;
}

function clearLegacyAmazonStorage() {
    if (typeof window === "undefined") return;
    localStorage.removeItem("amazonSellerId");
    localStorage.removeItem("amazonSellerName");
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-[15px] font-semibold text-slate-800">{title}</h2>
            {children}
        </div>
    );
}

function StatusBadge({ connected }: { connected: boolean }) {
    return (
        <span
            className={[
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium border",
                connected
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-slate-100 text-slate-500 border-slate-200",
            ].join(" ")}
        >
            <span
                className={[
                    "h-1.5 w-1.5 rounded-full",
                    connected ? "bg-emerald-500" : "bg-slate-400",
                ].join(" ")}
            />
            {connected ? "連携済み" : "未連携"}
        </span>
    );
}

// 利用モールで Amazon を有効化しただけ（Seller ID 未設定）のときに
// MallInitializer が入れるプレースホルダ。入力欄や連携状況では「未設定」として扱う
const UNCONFIGURED_SELLER_ID = "__amazon_unconfigured__";

export default function AmazonSettingsPage() {
    const { shops, setShops } = useMallContext();
    const rawAmazonShop = shops.find((s) => s.mall === "amazon");
    const amazonShop =
        rawAmazonShop && rawAmazonShop.mallShopId !== UNCONFIGURED_SELLER_ID
            ? rawAmazonShop
            : undefined;
    const isConnected = Boolean(amazonShop);

    const [sellerId, setSellerId] = useState(amazonShop?.mallShopId ?? "");
    const [sellerName, setSellerName] = useState(amazonShop?.displayName ?? "");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAuthenticating, setIsAuthenticating] = useState(false);

    useEffect(() => {
        if (amazonShop) {
            setSellerId(amazonShop.mallShopId);
            setSellerName(amazonShop.displayName ?? "");
        }
    }, [amazonShop]);

    const handleSave = async () => {
        const trimmedId = sellerId.trim();
        if (!trimmedId) {
            setError("Seller ID を入力してください");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            clearLegacyAmazonStorage();
            localStorage.setItem(getAmazonStorageKey("amazonSellerId"), trimmedId);
            localStorage.setItem(getAmazonStorageKey("amazonSellerName"), sellerName.trim());

            await apiPostJson("/api/company/shops", {
                mall: "amazon",
                mallShopId: trimmedId,
                displayName: sellerName.trim() || trimmedId,
            });

            // MallContext の shops を更新
            const newShop = shopRefFromSellerId(trimmedId, sellerName.trim() || trimmedId);
            const nextShops = [
                ...shops.filter((s) => s.mall !== "amazon"),
                newShop,
            ];
            setShops(nextShops);

            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e) {
            setError("保存に失敗しました。もう一度お試しください。");
        } finally {
            setSaving(false);
        }
    };

    const handleDisconnect = async () => {
        if (
            !window.confirm(
                "Amazon 連携を解除しますか？\n\n解除すると以下の影響があります：\n・モール切替から Amazon が表示されなくなります\n・Amazon の売上・利益データが閲覧できなくなります"
            )
        )
            return;
        setSaving(true);
        setError(null);
        try {
            await apiDeleteJson("/api/company/shops/amazon");
            clearLegacyAmazonStorage();
            localStorage.removeItem(getAmazonStorageKey("amazonSellerId"));
            localStorage.removeItem(getAmazonStorageKey("amazonSellerName"));
            setSellerId("");
            setSellerName("");
            setShops(shops.filter((s) => s.mall !== "amazon"));
        } catch (e) {
            setError("解除に失敗しました。もう一度お試しください。");
        } finally {
            setSaving(false);
        }
    };

    // RN-268: 連携の始まりはログインした状態で API に頼み、返ってきた Amazon の同意画面へ移動する
    // （アドレスに Seller ID を入れて直接開く形だと、ログインの確認ができないため）
    const startOAuth = async (startPath: string) => {
        try {
            const res = await apiPostJson<{ ok?: boolean; url?: string }>(startPath, {});
            if (!res?.url) throw new Error("amazon_auth_url_missing");
            window.location.href = res.url;
        } catch (e) {
            setError("認証を開始できませんでした。Seller ID を保存してから、もう一度お試しください。");
            setIsAuthenticating(false);
        }
    };

    const handleOAuthClick = () => {
        if (!sellerId.trim()) {
            setError("Seller ID を入力・保存してから認証してください");
            return;
        }
        setError(null);
        setIsAuthenticating(true);
        void startOAuth("/api/amazon/auth/start");
    };

    // スポンサー広告費の取込用（Amazon Ads API）の認証。SP-API 認証とは別に1回必要
    const handleAdsOAuthClick = () => {
        if (!sellerId.trim()) {
            setError("Seller ID を入力・保存してから認証してください");
            return;
        }
        setError(null);
        void startOAuth("/api/amazon/auth/ads/start");
    };

    return (
        <div className="mx-auto max-w-2xl space-y-6">
            <div>
                <h1 className="text-xl font-bold text-slate-900">Amazon 連携設定</h1>
                <p className="mt-1 text-sm text-slate-500">
                    Amazon Seller Central の情報を登録すると、売上・利益データを取得できます。
                </p>
            </div>

            {/* 連携状況 */}
            <SectionCard title="連携状況">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FF9900]/10">
                        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="#FF9900">
                            <path d="M13.76 11.45c-.35.61-.93 1-1.6 1.05-.95.07-1.5-.75-1.5-1.87 0-1.87 1.06-3.12 2.65-3.12.32 0 .63.05.92.14l-.47 3.8zm3.4 3.18c-.14.12-.35.13-.51.03-1.23-1.03-1.47-1.51-2.13-2.49-2.04 2.08-3.49 2.71-6.14 2.71C5.87 14.88 4 13.15 4 10.38 4 8.22 5.14 6.7 6.97 6c1.77-.67 4.23-.79 6.12-.97V4.67c0-.77.06-1.68-.39-2.34-.4-.59-1.15-.83-1.82-.83-1.24 0-2.34.64-2.61 1.96-.06.29-.27.57-.56.58l-3.13-.34c-.26-.06-.55-.27-.48-.67C4.7 1.27 7.12 0 9.86 0c1.41 0 3.25.37 4.36 1.44C15.38 2.52 15.26 4 15.26 5.6v4.65c0 1.4.58 2.01 1.12 2.77.19.27.23.59-.01.79l-1.21 1.05-.01-.23z" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">Amazon Seller Central</span>
                            <StatusBadge connected={isConnected} />
                        </div>
                        {amazonShop && (
                            <p className="mt-0.5 text-xs text-slate-500">
                                Seller ID: {amazonShop.mallShopId}
                                {amazonShop.displayName && amazonShop.displayName !== amazonShop.mallShopId
                                    ? ` (${amazonShop.displayName})`
                                    : ""}
                            </p>
                        )}
                    </div>
                    {isConnected && (
                        <button
                            type="button"
                            onClick={handleDisconnect}
                            className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100"
                        >
                            連携解除
                        </button>
                    )}
                </div>
            </SectionCard>

            {/* Seller ID 設定 */}
            <SectionCard title="Seller ID 設定">
                <div className="space-y-4">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                            Amazon Seller ID <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={sellerId}
                            onChange={(e) => setSellerId(e.target.value)}
                            placeholder="例: A1B2C3D4E5F6G7"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        />
                        <p className="mt-1 text-xs text-slate-400">
                            Seller Central → アカウント設定 → 出品者情報 から確認できます
                        </p>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                            店舗表示名（任意）
                        </label>
                        <input
                            type="text"
                            value={sellerName}
                            onChange={(e) => setSellerName(e.target.value)}
                            placeholder="例: Amazon本店"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        />
                    </div>

                    {error && (
                        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                    )}

                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={saving || !sellerId.trim()}
                                className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? "保存中..." : "保存する"}
                            </button>
                            {saved && (
                                <span className="text-sm font-medium text-emerald-600">✓ 保存しました</span>
                            )}
                        </div>

                        {amazonShop && (
                            <button
                                type="button"
                                onClick={handleOAuthClick}
                                disabled={isAuthenticating || !sellerId.trim()}
                                className="rounded-lg bg-amber-600 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isAuthenticating ? "Amazon Seller Central へ移動中..." : "Amazon Seller Central で認証"}
                            </button>
                        )}
                    </div>
                </div>
            </SectionCard>

            {/* 今後の機能予告 */}
            {/* 広告費の認証（Amazon Ads API） */}
            <SectionCard title="広告費の連携（スポンサー広告）">
                <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                        スポンサー広告（SP/SB/SD）の費用を自動で取り込み、広告費込みの利益を表示します。
                        売上の認証とは別に、広告用の認証が初回に1回必要です。
                    </p>
                    <button
                        type="button"
                        onClick={handleAdsOAuthClick}
                        disabled={!amazonShop || !sellerId.trim()}
                        className="rounded-lg bg-amber-600 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        広告費の連携を認証する
                    </button>
                    {!amazonShop && (
                        <p className="text-xs text-slate-400">先に Seller ID の保存と Seller Central 認証を行ってください</p>
                    )}
                    <p className="text-xs text-slate-400">
                        広告アカウントの権限を持つ Amazon アカウントでログインし、「許可」を押してください。認証の翌日から広告費が取り込まれます。
                    </p>
                </div>
            </SectionCard>

            <SectionCard title="データ連携（準備中）">
                <div className="space-y-3 text-sm text-slate-600">
                    <div className="flex items-start gap-2">
                        <span className="mt-0.5 text-slate-400">○</span>
                        <div>
                            <span className="font-medium text-slate-700">SP-API 認証</span>
                            <p className="text-xs text-slate-400">Seller ID を保存後、「Amazon Seller Central で認証」ボタンで OAuth 連携を完了してください</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-2">
                        <span className="mt-0.5 text-slate-400">○</span>
                        <div>
                            <span className="font-medium text-slate-700">売上・利益の自動集計</span>
                            <p className="text-xs text-slate-400">楽天と同じ粒度での日次・月次利益管理（開発中）</p>
                        </div>
                    </div>
                </div>
            </SectionCard>
        </div>
    );
}
